import { AppType } from "client/Api/Apps/common";
import {
	Button,
	FormItem,
	InputRow,
	Select,
	SelectItem,
	ValidationError
} from "client/Components";
import { AppDestinationWindowSelectFormItem } from "client/Features/Apps/AppDestinationWindowSelectFormItem";
import { openLiquitConnectionDialog } from "client/Features/Settings/Connections/Liquit/LiquitConnectionDialog";
import { LiquitConnectionListItemModel } from "client/Api/LiquitConfiguration/liquitConfiguration";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts
} from "client/Hooks";
import { createValidationConfiguration } from "client/Validation";
import {
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useState
} from "react";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { EditAppHandle } from "../EditApp";
import {
	EditAppBase,
	EditAppSettings,
	initialGenericState
} from "../EditAppBase";
import { useSaveApp, EditAppSavedResult } from "../useSaveApp";
import {
	LiquitConnectionSelectOptionsModel,
	getLiquitConnections,
	LiquitConnectionSelectOptionModel
} from "client/Api/Apps/EditApp/appParameters";
import {
	SaveLiquitAppModel,
	EditLiquitAppViewModel,
	saveLiquitApp
} from "client/Api/Apps/EditApp/saveApp";

const validation = createValidationConfiguration<SaveLiquitAppModel>();

validation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validation.forScalar("connectionId")
	.add(
		isNotNullNorUndefined()
	);

export type EditLiquitAppProps = {
	initialSettings: EditAppSettings<EditLiquitAppViewModel>;
	initiallyActivateWhoHasAccessTab: boolean;
};

export const EditLiquitApp = forwardRef(
	(
		props: EditLiquitAppProps,
		ref: Ref<EditAppHandle>
	): ReactElement => {
		const {
			initialSettings,
			initiallyActivateWhoHasAccessTab
		} = props;

		const [connections, setConnections] = useState<LiquitConnectionSelectOptionsModel>({
			canCreateNew: false,
			options: []
		});

		const [formData, setFormData] = useState<SaveLiquitAppModel>(
			() => convertToSaveModel(initialSettings)
		);

		const connectionOptions: readonly SelectItem<number>[] = useMemo(
			(): readonly SelectItem<number>[] => {
				return connections.options
					.map(
						o => ({
							value: o.id,
							text: o.name
						})
					);
			},
			[connections.options]
		);

		const alertManager: AlertManager = useAlerts();

		const {
			app,
			usersAndUserGroupsData
		} = initialSettings.model;

		const formHandler: IFormHandler<SaveLiquitAppModel> = useFormHandler(
			formData,
			setFormData,
			validation
		);

		const loadLiquitConnections = useCallback(
			async (signal: AbortSignal): Promise<void> => {
				try {
					const result: LiquitConnectionSelectOptionsModel =
						await getLiquitConnections(signal);

					setConnections(result);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			},
			[alertManager]
		);

		const addConnectionHandler = useCallback(
			(): void => {
				openLiquitConnectionDialog({
					onAfterSave:  (item: LiquitConnectionListItemModel): void => {
						setConnections((newConnections: LiquitConnectionSelectOptionsModel) => {
							const existingItemIndex: number = newConnections.options
								.findIndex(i => i.id === item.id);

							const options: LiquitConnectionSelectOptionModel[] = [
								...newConnections.options
							];

							if (existingItemIndex === -1) {
								options.push(item);
							} else {
								options.splice(existingItemIndex, 1, item);
							}

							return {
								...newConnections,
								options
							};
						});

						formHandler.for("connectionId")
							.attachToSelect()
							.onChange(item.id);
					}
				});
			},
			[formHandler]
		);

		useEffect(
			() => {
				const cancellation = new AbortController();
				loadLiquitConnections(cancellation.signal);

				return () => {
					cancellation.abort();
				};
			},
			[loadLiquitConnections]
		);

		const save: () => Promise<EditAppSavedResult> = useSaveApp<SaveLiquitAppModel>(
			formHandler,
			saveLiquitApp,
			AppType.Liquit,
		);

		useImperativeHandle(
			ref,
			(): EditAppHandle => ({
				save
			}),
			[save]
		);

		const hasValidationErrorForSpecificSettings: boolean | undefined = formHandler
			.under("connectionId", "destinationWindow")
			.attachToTab()
			.hasValidationError;

		return (
			<EditAppBase
				formHandler={formHandler}
				hasValidationErrorForSpecificSettings={hasValidationErrorForSpecificSettings}
				initialAppSettings={app}
				usersAndUserGroupsData={usersAndUserGroupsData}
				initiallyActivateWhoHasAccessTab={initiallyActivateWhoHasAccessTab}
			>
				<FormItem label={localize("_Connection")}>
					<InputRow>
						<InputRow.Stretch>
							<Select
								{...formHandler.for("connectionId")
									.attachToSelect()}
								options={connectionOptions}
								placeholder={localize("_SelectConnection")}
								menuTitle={localize("_Connection")}
							/>
						</InputRow.Stretch>
						{connections.canCreateNew && (
							<Button
								text={localize("_Add")}
								onClick={addConnectionHandler}
								kind="secondary"
							/>
						)}
					</InputRow>
					<ValidationError
						{...formHandler.for("connectionId").attachToValidationError()}
					/>
				</FormItem>
				<AppDestinationWindowSelectFormItem
					attachable={formHandler.for("destinationWindow")}
				/>
			</EditAppBase>
		);
	}
);

EditLiquitApp.displayName = "EditLiquitApp";

function convertToSaveModel(
	initialSettings: EditAppSettings<EditLiquitAppViewModel>
): SaveLiquitAppModel {
	const {
		connectionId,
		destinationWindow
	} = initialSettings
		.model
		.app;

	return {
		...initialGenericState(initialSettings),
		connectionId,
		destinationWindow,
	};
}
